import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../context/LocaleContext';
import SEO from '../components/SEO';

const Legal: React.FC = () => {
    const { t } = useTranslation();
    const { localePath } = useLocale();
    const location = useLocation();
    const [isShippingOpen, setShippingOpen] = useState(false);
    const [isReturnsOpen, setReturnsOpen] = useState(false);
    const [isTermsOpen, setTermsOpen] = useState(false);
    const [isPrivacyOpen, setPrivacyOpen] = useState(false);
    const [isIntroOpen, setIntroOpen] = useState(false);

    useEffect(() => {
        const hash = location.hash;
        if (hash) {
            const element = document.getElementById(hash.slice(1));
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth' });
                }, 100);
                switch (hash) {
                    case '#shipping':
                        setShippingOpen(true);
                        break;
                    case '#returns':
                        setReturnsOpen(true);
                        break;
                    case '#terms':
                        setTermsOpen(true);
                        break;
                    case '#privacy':
                        setPrivacyOpen(true);
                        break;
                }
            }
        }
    }, [location]);

    const toggleSection = (isOpen: boolean, setOpen: React.Dispatch<React.SetStateAction<boolean>>) => {
        setOpen(!isOpen);
    };

    return (
        <>
            <SEO
                title={t('footer.legal')}
                description={t('legal.introP1')}
                type="website"
            />
            <div className="container mx-auto px-4 py-8 max-w-[min(80vw,1400px)]">
                {/* Introductory Text */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="mb-8"
                >
                    <button
                        className="w-full flex justify-between items-center p-4 text-left bg-white/10 dark:bg-brand-charcoal/40 backdrop-blur-md rounded-xl border border-white/10 hover:bg-white/20 transition-colors"
                        onClick={() => toggleSection(isIntroOpen, setIntroOpen)}
                    >
                        <span className="text-2xl font-semibold text-black dark:text-white">
                            {t('legal.pageTitle')}
                        </span>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className={`text-black dark:text-white transform transition-transform duration-200 ${isIntroOpen ? 'rotate-180' : ''
                                }`}
                        />
                    </button>
                    {isIntroOpen && (
                        <div className="prose prose-lg dark:prose-invert mx-auto mb-12 p-4">
                            <p>{t('legal.introP1')}</p>
                            <p>{t('legal.introP2')}</p>
                            <p>{t('legal.introP3')}</p>
                            <p>{t('legal.introP4')}</p>
                        </div>
                    )}
                </motion.div>
                {/* Shipping & Returns Policy */}
                <motion.div id="shipping"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                    className="mb-8"
                >
                    <button
                        className="w-full flex justify-between items-center p-4 text-left bg-white/10 dark:bg-brand-charcoal/40 backdrop-blur-md rounded-xl border border-white/10 hover:bg-white/20 transition-colors"
                        onClick={() => toggleSection(isShippingOpen, setShippingOpen)}
                    >
                        <span className="text-2xl font-semibold text-black dark:text-white">
                            {t('legal.shippingTitle')}
                        </span>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className={`text-black dark:text-white transform transition-transform duration-200 ${isShippingOpen ? 'rotate-180' : ''
                                }`}
                        />
                    </button>
                    {isShippingOpen && (
                        <div className="prose prose-lg dark:prose-invert mx-auto p-4">
                            <div className="bg-white dark:bg-brand-dark p-8 rounded-lg shadow-md">
                                <h3 className="text-xl font-semibold mb-4">{t('legal.shippingProcessingTitle')}</h3>
                                <h4 className="font-semibold">{t('legal.shippingReadyTitle')}</h4>
                                <p>{t('legal.shippingReadyDesc')}</p>
                                <ul>
                                    <li>{t('legal.shippingExpress')}</li>
                                    <li>{t('legal.shippingStandard')}</li>
                                </ul>
                                <p>{t('legal.shippingTracking')}</p>
                                <h4 className="font-semibold">{t('legal.shippingMadeToOrderTitle')}</h4>
                                <p>{t('legal.shippingMadeToOrderDesc')}</p>
                                <br />
                                <h3 className="text-xl font-semibold mb-4">{t('legal.shippingCostsTitle')}</h3>
                                <p>{t('legal.shippingFreeOver200')}</p>
                                <p>{t('legal.shippingUnder200')}</p>
                                <ul>
                                    <li>{t('legal.shippingStandardCost')}</li>
                                    <li>{t('legal.shippingExpressCost')}</li>
                                </ul>
                                <p>{t('legal.shippingEuropeOnly')}</p>
                                <br />
                                <h3 className="text-xl font-semibold mb-4">{t('legal.shippingMethodsTitle')}</h3>
                                <p>{t('legal.shippingMethodsDesc')}</p>

                            </div>
                        </div>
                    )}
                </motion.div>

                <motion.div id="returns"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.8 }}
                    className="mb-8"
                >
                    <button
                        className="w-full flex justify-between items-center p-4 text-left bg-white/10 dark:bg-brand-charcoal/40 backdrop-blur-md rounded-xl border border-white/10 hover:bg-white/20 transition-colors"
                        onClick={() => toggleSection(isReturnsOpen, setReturnsOpen)}
                    >
                        <span className="text-2xl font-semibold text-black dark:text-white">
                            {t('legal.returnsTitle')}
                        </span>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className={`text-black dark:text-white transform transition-transform duration-200 ${isReturnsOpen ? 'rotate-180' : ''}`}
                        />
                    </button>
                    {isReturnsOpen && (
                        <div className="prose prose-lg dark:prose-invert mx-auto p-4">
                            <div className="bg-white dark:bg-brand-dark p-8 rounded-lg shadow-md">
                                <h3 className="text-xl font-semibold mb-4">{t('legal.returnsEligibilityTitle')}</h3>
                                <p>{t('legal.returnsEligibilityDesc')}</p>

                                <h3 className="text-xl font-semibold mb-4">{t('legal.returnsProcessTitle')}</h3>
                                <p>{t('legal.returnsProcessDesc')}</p>

                                <h3 className="text-xl font-semibold mb-4">{t('legal.returnsDamagedTitle')}</h3>
                                <p>{t('legal.returnsDamagedDesc')}</p>

                                <h3 className="text-xl font-semibold mb-4">{t('legal.returnsNonReturnableTitle')}</h3>
                                <p>{t('legal.returnsNonReturnableDesc')}</p>

                                <h3 className="text-xl font-semibold mb-4">{t('legal.returnsExchangeTitle')}</h3>
                                <p>{t('legal.returnsExchangeDesc')}</p>

                                <h3 className="text-xl font-semibold mb-4">{t('legal.returnsNotesTitle')}</h3>
                                <p>{t('legal.returnsNotesDesc')}</p>
                            </div>
                        </div>
                    )}
                </motion.div>

                {/* Terms of Service Section */}
                <motion.div id="terms"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="mb-8"
                >
                    <button
                        className="w-full flex justify-between items-center p-4 text-left bg-white/10 dark:bg-brand-charcoal/40 backdrop-blur-md rounded-xl border border-white/10 hover:bg-white/20 transition-colors"
                        onClick={() => toggleSection(isTermsOpen, setTermsOpen)}
                    >
                        <span className="text-2xl font-semibold text-black dark:text-white">
                            {t('legal.termsTitle')}
                        </span>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className={`text-black dark:text-white transform transition-transform duration-200 ${isTermsOpen ? 'rotate-180' : ''
                                }`}
                        />
                    </button>
                    {isTermsOpen && (
                        <div className="prose prose-lg dark:prose-invert mx-auto p-4">
                            <div className="bg-white dark:bg-brand-dark p-8 rounded-lg shadow-md">
                                {Array.from({ length: 20 }, (_, i) => i + 1).map(n => (
                                    <React.Fragment key={n}>
                                        <h2 className="text-2xl font-semibold mt-8 mb-4">{t(`legal.termsS${n}Title`)}</h2>
                                        <p className="mb-6">{t(`legal.termsS${n}Text`)}</p>
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    )}
                </motion.div>

                {/* Privacy Policy Section */}
                <motion.div id="privacy"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="mb-8"
                >
                    <button
                        className="w-full flex justify-between items-center p-4 text-left bg-white/10 dark:bg-brand-charcoal/40 backdrop-blur-md rounded-xl border border-white/10 hover:bg-white/20 transition-colors"
                        onClick={() => toggleSection(isPrivacyOpen, setPrivacyOpen)}
                    >
                        <span className="text-2xl font-semibold text-black dark:text-white">
                            {t('legal.privacyTitle')}
                        </span>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className={`text-black dark:text-white transform transition-transform duration-200 ${isPrivacyOpen ? 'rotate-180' : ''
                                }`}
                        />
                    </button>
                    {isPrivacyOpen && (
                        <div className="prose prose-lg dark:prose-invert mx-auto p-4">
                            <div className="bg-white dark:bg-brand-dark p-8 rounded-lg shadow-md">
                                {[1, 2, 3, 5, 6, 7, 8].map(n => (
                                    <React.Fragment key={n}>
                                        <h2 className="text-2xl font-semibold mb-4">{t(`legal.privacyS${n}Title`)}</h2>
                                        <p className="mb-6">{t(`legal.privacyS${n}Text`)}</p>
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    )}
                </motion.div>

                {/* Questions & Contact Information */}
                <motion.div id="info"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.8 }}
                    className="mb-8"
                >
                    <section className='mt-20'>
                        <h1 className="text-4xl md:text-5xl font-bold text-brand-dark dark:text-brand-white mb-12 text-center">
                            {t('legal.contactTitle')}
                        </h1>
                        <div className="prose prose-lg dark:prose-invert mx-auto">
                            <div className="bg-white dark:bg-brand-dark p-8 rounded-lg shadow-md">
                                <p className="mb-6">
                                    {t('legal.contactText')}
                                </p>
                                <p className="mb-6">
                                    {t('legal.contactAddress').split('\n').map((line: string, i: number, arr: string[]) => (
                                        <React.Fragment key={i}>
                                            {line}
                                            {i < arr.length - 1 && <br />}
                                        </React.Fragment>
                                    ))}
                                </p>
                            </div>
                        </div>
                    </section>
                </motion.div>


            </div>
        </>
    );
};

export default Legal;
